import { Button } from '@/components/common/Button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export default observer(function ForgotPasswordPage() {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();

    // store
    const { authStore: { forgotPassword } } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        phoneNumber: yup.string().required('form_error_validate.required')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            phoneNumber: ''
        }
    });

    // function
    const onSubmit = async (data: FormData) => {
        const res = await flowResult(forgotPassword(data));

        if (res && res.ok) {
            navigate(`${ROUTES.verifyOtp.href}?uuid=${res.data.uuid}`);
        }
    };

    const handleClickBackToSignin = () => {
        navigate(ROUTES.signIn.href);
    };

    return (
        <div className='flex w-full px-12 flex-col justify-center space-y-6 py-6 rounded-xl border bg-card text-card-foreground shadow'>
            <h2 className='title-auth-form text-center'>{t('words_title.forgot_password')}</h2>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className='space-y-4'>
                        <FormField
                            control={form.control}
                            name='phoneNumber'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('sentences.forgot_password_desc')}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type='number'
                                            isTypingOnlyNumber={true}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className='text-center mt-8'>
                        <Button type='submit' className='min-w-60' disabled={!form.getFieldState('phoneNumber').isDirty}>
                            {t('button.get_verify_code')}
                        </Button>
                    </div>
                    <div className='text-center mt-4 underline text-sm cursor-pointer text-link-1' onClick={handleClickBackToSignin}>
                        {t('button.back_to_signin')}
                    </div>
                </form>
            </Form>
        </div>
    );
});
